import React from "react";
import { ReactComponent as NotStreakIcon } from "../../../../../assects/svg/Insight/Not_streak_icon.svg";
import { ReactComponent as PartialLogStreakIcon } from "../../../../../assects/svg/Insight/Prtial_log_streak_icon.svg";
import { ReactComponent as LogStreakIcon } from "../../../../../assects/svg/Insight/Log_streak_icon.svg";
import { ReactComponent as Streak0 } from "../../../../../assects/svg/Insight/streak-0.svg";
import { ReactComponent as Streak1 } from "../../../../../assects/svg/Insight/streak-1.svg";
import { ReactComponent as Streak2 } from "../../../../../assects/svg/Insight/streak-2.svg";
import { ReactComponent as Streak3 } from "../../../../../assects/svg/Insight/streak-3.svg";
import { ReactComponent as Streak4 } from "../../../../../assects/svg/Insight/streak-4.svg";
import { ReactComponent as Streak5 } from "../../../../../assects/svg/Insight/streak-5.svg";
import { ReactComponent as Streak6 } from "../../../../../assects/svg/Insight/streak-6.svg";
import { ReactComponent as Streak7 } from "../../../../../assects/svg/Insight/streak-7.svg";

type DayLogStatus = "none" | "partial" | "logged" | "upcoming";

interface StreakCardProps {
  streakDays?: number;
  longestStreak?: number;
  weekLogs?: DayLogStatus[];       // Mo–Su, pass only past/today statuses
  weekStartDate?: Date;            // Monday of the displayed week
}

const GrayCircle = () => (
  <div className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-7 2xl:h-7 rounded-full bg-[#E5E7EB]" />
);

const StreakCard: React.FC<StreakCardProps> = ({
  streakDays,
  longestStreak,
  weekLogs,
  weekStartDate,
}) => {
  const isLoading = weekLogs == null;
  const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  // Strip time → compare dates only
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use weekStartDate (Monday) directly; fall back to the Monday of the current week.
  const weekMonday = (() => {
    const d = weekStartDate ? new Date(weekStartDate) : new Date(today);
    d.setHours(0, 0, 0, 0);
    if (!weekStartDate) d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // snap to Monday
    return d;
  })();

  const getEffectiveStatus = (index: number): DayLogStatus => {
    const dayDate = new Date(weekMonday);
    dayDate.setDate(weekMonday.getDate() + index);
    dayDate.setHours(0, 0, 0, 0);

    if (dayDate > today) {
      const status = weekLogs![index];
      return status === "logged" || status === "partial" ? status : "upcoming";
    }
    return weekLogs![index] ?? "none";
  };

  const renderDayIcon = (status: DayLogStatus) => {
    switch (status) {
      case "logged":
        return <LogStreakIcon className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-7 2xl:h-7" />;
      case "partial":
        return <PartialLogStreakIcon className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-7 2xl:h-7" />;
      case "none":
        return <NotStreakIcon className="w-4 h-4 lg:w-5 lg:h-5 2xl:w-7 2xl:h-7" />;
      case "upcoming":
        return <GrayCircle />;
    }
  };

  // Map logged day count (0–7) to the corresponding Streak SVG component
  const streakIconMap = [Streak0, Streak1, Streak2, Streak3, Streak4, Streak5, Streak6, Streak7];

  const loggedCount = isLoading ? 0 : days.reduce((count, _, i) => {
    return getEffectiveStatus(i) === "logged" ? count + 1 : count;
  }, 0);

  const StreakIcon = streakIconMap[Math.min(loggedCount, 7)];

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-5 flex flex-col items-center justify-between h-full">
      {/* Streak Icon + streak count */}
      <div className="flex flex-col items-center gap-1 mt-1">
        {isLoading ? (
          <div className="w-12 h-12 lg:w-14 lg:h-14 2xl:w-[68px] 2xl:h-[68px] rounded-full bg-[#E5E7EB]" />
        ) : (
          <StreakIcon className="w-12 h-12 lg:w-14 lg:h-14 2xl:w-[68px] 2xl:h-[68px]" />
        )}
        <span className="text-[12px] 3xl:text-[14px] text-[#1F1F1F] font-inter mt-1">
          days streak this week!
        </span>
      </div>

      {/* Day icons */}
      <div className="flex items-center gap-2 3xl:gap-3 4xl:gap-5 my-2 pb-3 w-full justify-center"
        style={{
          borderBottom: "none",
          backgroundImage: "repeating-linear-gradient(to right, #E5E7EB 0px, #E5E7EB 4px, transparent 4px, transparent 8px)",
          backgroundSize: "98% 1px",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "bottom",
        }}
      >
        {days.map((d, i) => (
          <div key={d} className="flex flex-col items-center gap-1">
            {isLoading ? <GrayCircle /> : renderDayIcon(getEffectiveStatus(i))}
            <span className="text-[9px] text-[#8E8E93] font-inter">{d}</span>
          </div>
        ))}
      </div>

      {/* Longest streak */}
      <div className="text-[12px] 3xl:text-[14px] text-[#8D8D8D] font-inter">
        Longest streak :{" "}
        {isLoading
          ? <span className="inline-block w-6 h-3 rounded bg-[#E5E7EB] align-middle" />
          : <span className="font-semibold text-[#1F1F1F]">{longestStreak}d</span>
        }
      </div>
    </div>
  );
};

export default StreakCard;